import { apiJson, ApiError } from '../api';
import { getAnonymousInterestsCookie, reconcileAnonymousInterests } from '../utils/anonymousInterestsCookie';
import type { ForYouV2State } from '../utils/forYouV2';
import {
  feedV2NotBuiltState, feedV2StateFromStored, forYouV2StateFromPublic, includedFeedIds,
  type PublicForYouV2, type StoredFeedV2, type StoredFeedV2State,
} from '../utils/signedOutV2';
import { listBetaSearches, type BetaSearch } from './betaSearchRepository';

/**
 * The signed-out reader's reads (see `utils/signedOutV2`). Public: a visitor
 * without an account must never be bounced to /login, so every call here
 * passes `{ public: true }`. None of them builds anything.
 */

/** A feed's last stored build for `hours`, as the feed page's state. */
export async function loadStoredFeedV2({ feedId, hours }: { feedId: string; hours: number }): Promise<StoredFeedV2State> {
  const params = new URLSearchParams({ hours: String(hours) });
  try {
    return feedV2StateFromStored(await apiJson<StoredFeedV2>(
      `/api/v1/feeds/${encodeURIComponent(feedId)}/stored?${params}`, { public: true }));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return feedV2NotBuiltState();
    throw err;
  }
}

/** The visitor's chips: the default Home feeds and the public feeds, with their own switches (the cookie). */
export async function signedOutInterests(): Promise<BetaSearch[]> {
  const data = await listBetaSearches();
  return reconcileAnonymousInterests(data.searches, data.public_searches, getAnonymousInterestsCookie());
}

/** The signed-out For You for the visitor's switched-on feeds, as the For You page's state. */
export async function loadPublicForYouV2({ hours }: { hours: number }): Promise<ForYouV2State & { builtAt: string | null }> {
  const interests = await signedOutInterests();
  const ids = includedFeedIds(interests);
  if (!ids.length) return forYouV2StateFromPublic({ window_hours: hours, feeds: [], missing: [], stories: [] }, interests);
  const params = new URLSearchParams({ ids: ids.join(','), hours: String(hours) });
  return forYouV2StateFromPublic(await apiJson<PublicForYouV2>(`/api/v1/feeds/public/for-you?${params}`, { public: true }), interests);
}
